import { unwrap, type components } from "@sagot/api-client";
import type { RestoreTarget } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import type { WorkflowDetail, WorkflowOut } from "../types";
import { workflowKeys } from "../use-workflows";

type Schemas = components["schemas"];
export type DefaultImpact = Schemas["ImpactOut"];
export type CatalogueEntry = Schemas["CatalogueEntryOut"];
export type DefaultCatalogue = Schemas["CatalogueOut"];

/** The catalogue changes with a release, not while a person works: it is read once per visit. */
const CATALOGUE_STALE_MS = 10 * 60_000;

export const defaultKeys = {
  /** The default workflows alone (the cross-links on other pages read it). Under `all`, so a pause, a publish or a restore refresh it. */
  list: (ws: string) => [...workflowKeys.all(ws), "system"] as const,
  /** Under the workflow's own key, so a pause, a publish or a restore (which refresh `all`) refresh the numbers too. */
  impact: (ws: string, id: string) => [...workflowKeys.detail(ws, id), "impact"] as const,
  catalogue: (ws: string) => [...workflowKeys.all(ws), "defaults-catalogue"] as const,
};

/** The default workflows alone: the pages whose settings a default now uses link to the one that does (and warn when it is paused). */
export function useDefaultWorkflowList(enabled: boolean): UseQueryResult<WorkflowOut[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: defaultKeys.list(ws),
    queryFn: () => unwrap(api.GET("/v1/workflows", { params: { query: { origin: "system" } } })),
    enabled,
    retry: false,
  });
}

/** What the default did in the last 30 days: what pausing it would stop (the numbers of the pause dialog). */
export function useWorkflowImpact(id: string, enabled: boolean): UseQueryResult<DefaultImpact> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: defaultKeys.impact(ws, id),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/impact", { params: { path: { workflow_id: id } } })),
    enabled,
    retry: false,
  });
}

/** The default workflows as the catalogue stands now (names, descriptions, revisions): "Update available" reads its revision. */
export function useDefaultCatalogue(enabled: boolean): UseQueryResult<DefaultCatalogue> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: defaultKeys.catalogue(ws),
    queryFn: () => unwrap(api.GET("/v1/workflows/defaults")),
    enabled,
    retry: false,
    staleTime: CATALOGUE_STALE_MS,
  });
}

export interface RestoreRequest {
  publish: boolean;
  revision: RestoreTarget;
  force: boolean;
}

/** "Restore default": the baseline (or the newest catalogue revision) goes back into the draft and, unless `publish` is off, is published. */
export function useRestoreDefault(id: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: RestoreRequest) => unwrap(api.POST("/v1/workflows/{workflow_id}/restore", { params: { path: { workflow_id: id } }, body })),
    onSuccess: (detail: WorkflowDetail) => {
      client.setQueryData(workflowKeys.detail(ws, id), detail);
      return client.invalidateQueries({ queryKey: workflowKeys.all(ws) });
    },
  });
}

/** A normal draft of the workspace's own made from a workflow (a default included); Standard and up. */
export function useDuplicateWorkflow(id: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string }) => unwrap(api.POST("/v1/workflows/{workflow_id}/duplicate", { params: { path: { workflow_id: id } }, body })),
    onSuccess: (detail: WorkflowDetail) => {
      client.setQueryData(workflowKeys.detail(ws, detail.id), detail);
      return client.invalidateQueries({ queryKey: workflowKeys.all(ws) });
    },
  });
}
